import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { useExperienceSessionDetails } from '../../../hooks/analytics/useAnalytics';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useSessionExportData } from '../../../utils/export';
import { BasePopup } from '../../dashboard/BasePopup';
import { Activity } from 'lucide-react';
import { ExportButton } from '../../../utils/export';
import { ExportFormat } from '../../../utils/export/types';
import { UserRankingTable, processSessionDataToRankings } from '../shared';
import { AnalyticsErrorBoundary } from '../shared/AnalyticsErrorBoundary';
import { SessionDetailDto } from '@repo/shared/dtos/analytics/SessionDataDto';
import { MetricCard } from '../overview/MetricCard';
import { palette } from '../shared/theme';
import { CHART_CONFIG } from '../config';

interface ExperienceBreakdownModalProps {
  experienceTitle: string;
  filters?: BaseAnalyticsFilter;
  isOpen: boolean;
  onClose: () => void;
}

function FilterSummary({
  experienceTitle,
  filters,
}: {
  experienceTitle: string;
  filters?: BaseAnalyticsFilter;
}) {
  const hasFilters = filters && (filters.startDate || filters.endDate || filters.unitIds?.length);

  return (
    <div className="mb-4 rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface-elev)] p-4">
      <h4 className="mb-2 font-semibold text-[var(--anl-text)]">Experience Details</h4>
      <div className="text-sm text-[var(--anl-text-muted)]">
        <p>
          <span className="font-semibold text-[var(--anl-text)]">Experience:</span>{' '}
          {experienceTitle}
        </p>
        {hasFilters && (
          <div className="mt-2">
            <span className="font-semibold text-[var(--anl-text)]">Active Filters:</span>
            <ul className="ml-2 mt-1 list-inside list-disc">
              {filters?.startDate && <li>Start Date: {filters.startDate.toLocaleDateString()}</li>}
              {filters?.endDate && <li>End Date: {filters.endDate.toLocaleDateString()}</li>}
              {filters?.unitIds?.length && <li>Units: {filters.unitIds.length} selected</li>}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryStats({ sessions }: { sessions: SessionDetailDto[] }) {
  const isMobile = useIsMobile();
  const isNarrow = useIsMobile(1024);
  const totalSessions = sessions.length;
  const totalMinutes = sessions.reduce((sum, s) => sum + s.durationMinutes, 0);
  const avgDuration = totalSessions > 0 ? totalMinutes / totalSessions : 0;
  const sensorDataSessions = sessions.filter(s => s.hasSensorData).length;

  return (
    <div className={`grid gap-3 ${isMobile ? 'mb-4' : 'mb-8'} ${isNarrow ? 'grid-cols-2' : 'grid-cols-3'}`}>
      <MetricCard value={totalSessions} label="Total Sessions" accent={palette.indigo} />
      <MetricCard
        value={`${Math.round(avgDuration)}m`}
        label="Avg Duration"
        accent={palette.cyan}
      />
      <MetricCard
        value={sensorDataSessions}
        label="Sensor Usage"
        accent={CHART_CONFIG.COLORS.SUCCESS}
      />
    </div>
  );
}

function ExperienceBreakdownModalContent({
  experienceTitle,
  filters,
  isOpen,
  onClose,
}: ExperienceBreakdownModalProps) {
  const isMobile = useIsMobile();
  const {
    data: experienceData,
    isLoading,
    error,
  } = useExperienceSessionDetails(experienceTitle, filters, isOpen);

  const sessions = experienceData?.sessions || [];
  const exportData = useSessionExportData(sessions);

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={onClose}
      title={experienceTitle}
      icon={<Activity className="h-6 w-6" />}
      className="max-w-6xl"
      variant="analytics"
      headerContent={
        <ExportButton
          data={exportData}
          config={{
            fileName: `experience-breakdown-${experienceTitle.replace(/\s+/g, '-').toLowerCase()}`,
            date: true,
          }}
          format={ExportFormat.CSV}
          className="text-[var(--anl-text-muted)] transition-colors hover:text-[var(--anl-text)]"
        />
      }
    >
      <div className={isMobile ? 'p-3' : 'p-6'}>
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-cyan-500"></div>
          </div>
        )}

        {error && (
          <div className="py-12 text-center text-red-600">
            <p>Error loading experience details</p>
          </div>
        )}

        {!isLoading && !error && (
          <>
            <FilterSummary experienceTitle={experienceTitle} filters={filters} />
            <SummaryStats sessions={sessions} />
            <UserRankingTable
              data={processSessionDataToRankings(sessions)}
              title="User Rankings"
              subtitle="Ranked by total sessions"
            />
          </>
        )}
      </div>
    </BasePopup>
  );
}

export function ExperienceBreakdownModal(props: ExperienceBreakdownModalProps) {
  return (
    <AnalyticsErrorBoundary>
      <ExperienceBreakdownModalContent {...props} />
    </AnalyticsErrorBoundary>
  );
}
